import React, { useState } from 'react';
import { Col, Row } from '../../../lib/utils';
import { DemiLightText, RegularText } from '../../../lib/Text';
import { colors } from '../../../lib/colors';
import DialogShell from '../../Settlement/components/exceptionRates/DialogShell';
import {
    SettlementCategory,
    getSettlementClassificationErrorMessage,
    useAddSettlementCategory,
    useUpdateSettlementCategory,
} from '../../../features/settlementClassification';

const MAX_NAME = 30;

const INPUT_CLASS =
    'h-[40px] border border-solid border-[#E6E8EA] rounded-[10px] bg-white px-[12px] text-[15px] text-[#2D3642] outline-none focus:border-primary';

/**
 * 정산 분류 추가·이름 변경·보관. 삭제는 없다(보관만) — 이미 판매한 수강권에 기록된 분류가 남아 있어서.
 * 서버 응답의 전체 목록으로 캐시를 바꾸므로 열린 상품 모달의 선택지도 바로 갱신된다.
 */
const ModalSettlementCategoryManage = ({
    centerId,
    categories,
    onClose,
}: {
    centerId: string;
    categories: SettlementCategory[];
    onClose: () => void;
}) => {
    const [newName, setNewName] = useState('');
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const add = useAddSettlementCategory(centerId);
    const update = useUpdateSettlementCategory(centerId);
    const busy = add.isLoading || update.isLoading;

    const handleAdd = async () => {
        const name = newName.trim();
        if (!name || busy) return;
        setError(null);
        try {
            await add.mutateAsync(name);
            setNewName('');
        } catch (e) {
            setError(getSettlementClassificationErrorMessage(e as any));
        }
    };

    const handleRename = async (key: string) => {
        const name = editingName.trim();
        if (!name || busy) return;
        setError(null);
        try {
            await update.mutateAsync({ key, body: { name } });
            setEditingKey(null);
        } catch (e) {
            setError(getSettlementClassificationErrorMessage(e as any));
        }
    };

    const handleArchive = async (key: string, archived: boolean) => {
        if (busy) return;
        setError(null);
        try {
            await update.mutateAsync({ key, body: { archived } });
        } catch (e) {
            setError(getSettlementClassificationErrorMessage(e as any));
        }
    };

    return (
        <DialogShell title="정산 분류 관리" width={480} onClose={onClose} busy={busy}>
            <Col className="px-[24px] pb-[22px] overflow-y-auto">
                <RegularText fontSize={14} lineHeight={21} color="#6B7280" className="mt-[4px] whitespace-pre-line">
                    {'보관한 분류는 상품에서 새로 고를 수 없어요.\n이미 판매한 수강권에는 그대로 남아요. 지우기는 없어요.'}
                </RegularText>

                <Row className="items-center gap-[8px] mt-[16px]">
                    <input
                        className={`${INPUT_CLASS} flex-1`}
                        value={newName}
                        maxLength={MAX_NAME}
                        placeholder="새 분류 이름 (예: 공동구매)"
                        onChange={(e) => {
                            setError(null);
                            setNewName(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleAdd();
                        }}
                    />
                    <div
                        className={`h-[40px] w-[72px] rounded-[9px] bg-primary flex items-center justify-center ${
                            !newName.trim() || busy ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={handleAdd}
                    >
                        <RegularText fontSize={15} color="white">
                            추가
                        </RegularText>
                    </div>
                </Row>

                {error && (
                    <RegularText fontSize={14} color="#D93043" className="mt-[10px]">
                        {error}
                    </RegularText>
                )}

                <Col className="mt-[14px] gap-[2px] max-h-[320px] overflow-y-auto">
                    {categories.length === 0 && (
                        <RegularText fontSize={14} color="#9CA3AF" className="py-[14px] text-center">
                            아직 분류가 없어요.
                        </RegularText>
                    )}
                    {categories.map((category) => (
                        <Row
                            key={category.key}
                            className="items-center justify-between px-[10px] py-[8px] rounded-[10px] hover:bg-[#F6F8FA] min-h-[48px]"
                        >
                            {editingKey === category.key ? (
                                <Row className="items-center gap-[8px] flex-1">
                                    <input
                                        className={`${INPUT_CLASS} flex-1`}
                                        value={editingName}
                                        maxLength={MAX_NAME}
                                        autoFocus
                                        onChange={(e) => setEditingName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.nativeEvent.isComposing) handleRename(category.key);
                                            if (e.key === 'Escape') setEditingKey(null);
                                        }}
                                    />
                                    <RegularText
                                        fontSize={14}
                                        color={colors.primary}
                                        className="cursor-pointer"
                                        onClick={() => handleRename(category.key)}
                                    >
                                        저장
                                    </RegularText>
                                    <RegularText
                                        fontSize={14}
                                        color="#6B7280"
                                        className="cursor-pointer"
                                        onClick={() => setEditingKey(null)}
                                    >
                                        취소
                                    </RegularText>
                                </Row>
                            ) : (
                                <>
                                    <Row className="items-center gap-[8px]">
                                        <RegularText fontSize={15} color={category.archived ? '#9CA3AF' : '#3D4244'}>
                                            {category.name}
                                        </RegularText>
                                        {category.archived && (
                                            <DemiLightText
                                                fontSize={12}
                                                color="#6B7280"
                                                className="px-[6px] py-[1px] rounded-[4px] bg-[#F1F2F4]"
                                            >
                                                보관됨
                                            </DemiLightText>
                                        )}
                                    </Row>
                                    <Row className="items-center gap-[14px]">
                                        {!category.archived && (
                                            <RegularText
                                                fontSize={14}
                                                color="#6B7280"
                                                className="cursor-pointer hover:underline"
                                                onClick={() => {
                                                    setError(null);
                                                    setEditingKey(category.key);
                                                    setEditingName(category.name);
                                                }}
                                            >
                                                이름 변경
                                            </RegularText>
                                        )}
                                        <RegularText
                                            fontSize={14}
                                            color={category.archived ? colors.primary : '#6B7280'}
                                            className="cursor-pointer hover:underline"
                                            onClick={() => handleArchive(category.key, !category.archived)}
                                        >
                                            {category.archived ? '다시 쓰기' : '보관'}
                                        </RegularText>
                                    </Row>
                                </>
                            )}
                        </Row>
                    ))}
                </Col>

                <Row className="justify-end mt-[18px]">
                    <div
                        className="h-[40px] w-[88px] rounded-[9px] bg-[#F4F5F7] flex items-center justify-center cursor-pointer"
                        onClick={busy ? undefined : onClose}
                    >
                        <RegularText fontSize={15} color="#3D4244">
                            닫기
                        </RegularText>
                    </div>
                </Row>
            </Col>
        </DialogShell>
    );
};

export default ModalSettlementCategoryManage;
